import { cn } from '@/shared/shadcn/lib/utils'
import { ChartConfig } from '@/shared/shadcn/components/ui/chart'

interface DynamicTooltipProps {
  active?: boolean
  payload?: any[]
  label?: string
  dataKey: string
  lineChartDataKey?: string
  chartConfig: ChartConfig
}

export function DynamicTooltip({
  active,
  payload,
  label,
  dataKey,
  lineChartDataKey,
  chartConfig
}: DynamicTooltipProps) {
  if (!active || !payload?.length) return null

  const barEntry = payload.find(p => p.dataKey === dataKey)
  const lineEntry = payload.find(p => p.dataKey === lineChartDataKey)

  const barColor =
    barEntry?.payload?.[`${dataKey}_color`] ||
    chartConfig[dataKey]?.color ||
    'var(--chart-1)'

  const lineColor =
    lineChartDataKey && chartConfig[lineChartDataKey]
      ? chartConfig[lineChartDataKey].color
      : 'var(--card)'

  return (
    <div
      className="border-border/50 bg-card grid items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl"
      style={{
        width: '10rem', // 👈 fixed width
        minWidth: '10rem',
        maxWidth: '10rem',
        whiteSpace: 'nowrap' // prevents wrapping inside
      }}
    >
      {/* Tooltip label (date) */}
      <div className="font-medium text-ring label-bold-medium text-start">
        {new Date(label as string).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })}
      </div>

      <div className="grid gap-1.5">
        {/* Bar indicator */}
        {barEntry && (
          <div
            className={cn(
              '[&>svg]:text-muted-foreground flex w-full flex-wrap items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5'
            )}
          >
            <div
              className="shrink-0 rounded-[2px]"
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: barColor
              }}
            />
            <div className="flex flex-1 justify-between leading-none items-center">
              <div className="grid gap-1.5">
                <span className="text-ring label-medium">
                  {chartConfig[dataKey]?.label}
                </span>
              </div>
              {barEntry.value && (
                <span className="text-foreground font-mono font-medium tabular-nums label-bold-medium">
                  {barEntry.value.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Line indicator */}
        {lineEntry && (
          <div
            className={cn(
              '[&>svg]:text-muted-foreground flex w-full flex-wrap items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5'
            )}
          >
            <div
              className="shrink-0 rounded-[2px]"
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: lineColor,
                boxShadow: '0 0 0 2px var(--card)',
                borderRadius: '1px'
              }}
            />
            <div className="flex flex-1 justify-between leading-none items-center">
              <div className="grid gap-1.5">
                <span className="text-ring label-medium">
                  {chartConfig[lineChartDataKey!]?.label}
                </span>
              </div>
              {lineEntry.value && (
                <span className="text-foreground font-mono font-medium tabular-nums label-bold-medium">
                  {lineEntry.value.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
